import { getCurrentRestViewer, restFetchWithNonceRecovery } from "./restFetchOptions.js";

const STATS_CACHE_TTL_MS = 15000;
// The server may use up to 4.5 seconds on a cold fast-map rebuild. Account for
// WordPress startup and network latency outside that work budget.
const STATS_FETCH_TIMEOUT_MS = 8000;
const statsCache = new Map();

export function getCurrentShrkValue() {
  const shrkEl = typeof document !== 'undefined' ? document.querySelector('#shrk') : null;
  return shrkEl ? (shrkEl.value ?? shrkEl.getAttribute('value') ?? '0').toString() : '0';
}

export function normalizeStatsTierValue(tier) {
  const parsedTier = Number(tier);
  if (!Number.isFinite(parsedTier)) {
    return '0';
  }

  return Math.max(0, Math.min(3, Math.floor(parsedTier))).toString();
}

export function getCurrentUserTierValue() {
  const tierEl = typeof document !== 'undefined' ? document.querySelector('#user-tier') : null;
  return normalizeStatsTierValue(tierEl ? (tierEl.value ?? tierEl.getAttribute('value') ?? '0') : '0');
}

export function getCurrentShowOddsValue() {
  const isEnabledValue = (value) => ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());
  const urlParam = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('show_odds')
    : null;
  if (isEnabledValue(urlParam)) {
    return '1';
  }

  const showOddsEl = typeof document !== 'undefined' ? document.querySelector('#show-odds') : null;
  const showOddsValue = showOddsEl ? (showOddsEl.value ?? showOddsEl.getAttribute('value') ?? '0') : '0';
  return isEnabledValue(showOddsValue) ? '1' : '0';
}

export function getStatsCacheKey(shrk = getCurrentShrkValue(), userTier = getCurrentUserTierValue()) {
  return `${String(shrk)}:tier:${normalizeStatsTierValue(userTier)}`;
}

export function primeStatsCache(stats, shrk = getCurrentShrkValue(), userTier = getCurrentUserTierValue()) {
  if (!stats || typeof stats !== "object") {
    return stats;
  }

  statsCache.set(getStatsCacheKey(shrk, userTier), {
    data: stats,
    ts: Date.now(),
    promise: null,
  });

  return stats;
}

export function clearStatsCache(shrk = null, userTier = null) {
  if (shrk === null || shrk === undefined) {
    statsCache.clear();
    return;
  }

  if (userTier === null || userTier === undefined) {
    const shrkKeyPrefix = `${String(shrk)}:tier:`;
    for (const key of statsCache.keys()) {
      if (key === String(shrk) || key.startsWith(shrkKeyPrefix)) {
        statsCache.delete(key);
      }
    }
    return;
  }

  statsCache.delete(getStatsCacheKey(shrk, userTier));
}

export default async function FetchStats(options = {}) {
  const shrk = (options.shrk ?? getCurrentShrkValue()).toString();
  const userTier = normalizeStatsTierValue(options.userTier ?? getCurrentUserTierValue());
  const cacheKey = getStatsCacheKey(shrk, userTier);
  const force = options.force === true;
  const ttlMs = Number.isFinite(Number(options.ttlMs)) ? Number(options.ttlMs) : STATS_CACHE_TTL_MS;
  const now = Date.now();
  const cached = statsCache.get(cacheKey);

  if (!force && cached?.data && now - cached.ts < ttlMs) {
    return cached.data;
  }

  if (!force && cached?.promise) {
    return cached.promise;
  }

  const timeoutMs = Number.isFinite(Number(options.timeoutMs)) && Number(options.timeoutMs) > 0
    ? Number(options.timeoutMs)
    : STATS_FETCH_TIMEOUT_MS;

  const params = new URLSearchParams();
  params.set('tier', userTier);
  params.set('viewer', getCurrentRestViewer());
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  const requestUrl = `/wp-json/stats/v1/available/${encodeURIComponent(shrk)}?${params.toString()}`;
  const requestPromise = restFetchWithNonceRecovery(requestUrl, {
    signal: controller.signal,
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`Stats request failed with HTTP ${response.status}`);
    }

    const data = await response.json();
    primeStatsCache(data, shrk, userTier);
    return data;
  }).catch(error => {
    if (cached?.data) {
      statsCache.set(cacheKey, {
        data: cached.data,
        ts: cached.ts,
        promise: null,
      });
    } else {
      statsCache.delete(cacheKey);
    }
    throw error;
  }).finally(() => {
    window.clearTimeout(timeoutId);
  });

  statsCache.set(cacheKey, {
    data: cached?.data || null,
    ts: cached?.ts || 0,
    promise: requestPromise,
  });

  return requestPromise;
}
